import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Skeleton } from "../../../ui/skeleton";
import { Progress } from "../../../ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import { Plus, Eye, Copy, Mail, Loader2 } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { toast } from "sonner";
import { formatDate } from "../../../../lib/customersEngageV1";

type Row = {
  id: string;
  name: string | null;
  type: string | null;
  status: string | null;
  estimated_recipients: number | null;
  sent_at: string | null;
  created_at: string | null;
  template_id: string | null;
  progress_sent: number | null;
  progress_failed: number | null;
  progress_total: number | null;
  error_message: string | null;
};

const statusClass = (s: string | null) => {
  switch (s) {
    case "sent":
      return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200";
    case "sending":
      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200";
    case "draft":
      return "bg-muted text-muted-foreground border-border";
    case "failed":
      return "bg-destructive/10 text-destructive border-destructive/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

export default function Campaigns() {
  const { merchant } = useMerchant();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!merchant) return;
    setLoading(true);
    const { data, error } = await epicengageSupabasev1
      .from("campaigns")
      .select("id, name, type, status, estimated_recipients, sent_at, created_at, template_id, progress_sent, progress_failed, progress_total, error_message")
      .eq("merchant_id", merchant.id)
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [merchant]);

  // Realtime: live progress updates for sending campaigns
  useEffect(() => {
    if (!merchant) return;
    const channel = epicengageSupabasev1
      .channel(`campaigns-progress-${merchant.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "campaigns", filter: `merchant_id=eq.${merchant.id}` },
        (payload) => {
          const next = payload.new as Row;
          setRows((prev) => prev.map((r) => (r.id === next.id ? { ...r, ...next } : r)));
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "campaigns", filter: `merchant_id=eq.${merchant.id}` },
        (payload) => {
          const next = payload.new as Row;
          setRows((prev) => (prev.some((r) => r.id === next.id) ? prev : [next, ...prev]));
        }
      )
      .subscribe();
    return () => {
      epicengageSupabasev1.removeChannel(channel);
    };
  }, [merchant]);

  const duplicate = async (row: Row) => {
    if (!merchant) return;
    const { data, error } = await epicengageSupabasev1
      .from("campaigns")
      .insert({
        merchant_id: merchant.id,
        name: `${row.name || "Campaign"} (copy)`,
        type: row.type,
        template_id: row.template_id,
        status: "draft",
      })
      .select("id")
      .single();
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Campaign duplicated as draft");
    if (row.template_id) navigate(`/clientpanel/enagev1/campaigns/new?template=${row.template_id}`);
    else load();
    void data;
  };

  return (
      <div className="px-10 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Send and manage your email campaigns.
            </p>
          </div>
          <Button onClick={() => navigate("/clientpanel/enagev1/campaigns/new")}>
            <Plus className="h-4 w-4 mr-2" /> New Campaign
          </Button>
        </div>

        <div className="mt-8 rounded-lg border bg-card">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-medium">No campaigns yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first campaign to start engaging customers.
              </p>
              <Button onClick={() => navigate("/clientpanel/enagev1/campaigns/new")} className="mt-4">
                <Plus className="h-4 w-4 mr-2" /> New Campaign
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Recipients</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const isSending = r.status === "sending";
                  const total = r.progress_total ?? r.estimated_recipients ?? 0;
                  const done = (r.progress_sent ?? 0) + (r.progress_failed ?? 0);
                  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">
                        <div>{r.name || "Untitled"}</div>
                        {isSending && (
                          <div className="mt-2 max-w-xs">
                            <Progress value={pct} className="h-1.5" />
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {done.toLocaleString()} / {total.toLocaleString()} sent
                              {(r.progress_failed ?? 0) > 0 && (
                                <span className="text-destructive">· {r.progress_failed} failed</span>
                              )}
                            </p>
                          </div>
                        )}
                        {r.status === "failed" && r.error_message && (
                          <p className="text-xs text-destructive mt-1 line-clamp-2">{r.error_message}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {r.type || "manual"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`capitalize ${statusClass(r.status)}`}>
                          {r.status || "draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{(r.estimated_recipients ?? 0).toLocaleString()}</TableCell>
                      <TableCell>{formatDate(r.sent_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate("/clientpanel/enagev1/analytics")}>
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => duplicate(r)}>
                            <Copy className="h-4 w-4 mr-1" /> Duplicate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
  );
}
